'use strict';

(function() {

    if (!window.jim) window.jim = {};

    var ALWAYS_TRUE = function(){ return true; };
    var ALWAYS_FALSE = function(){ return false; };

    /**
     * A Wizard containing steps.
     *
     * @constructor
     */
    jim.Wizard = function ($q) {

        var self = this;
        var stepIdx = 0;
        var done = false;
        var loading = false;

        self.steps = [];
        self.listeners = [];

        /**
         * The current step name.
         */
        Object.defineProperty(self, 'stepName', {get: function(){
            return self.step.name;
        }});

        /**
         * The current step
         */
        Object.defineProperty(self, 'step', {get: function(){
            if (stepIdx < 0 || stepIdx >= self.steps.length)
                return {index: -1, name: undefined, enabled: ALWAYS_FALSE, validations: {}};
            return self.steps[stepIdx];
        }});

        /**
         * Whether an active progression is running
         */
        Object.defineProperty(self, 'loading', {get: function(){
            return loading;
        }});

        /**
         * Add a step to the wizard.  The steps have to be added in order.
         * @param stepName Name for the step.  This will also function as an identifier.
         * @param condition (optional) condition function. This will be evaluated to check if
         * the step is currently enabled. If not, the step will be skipped.
         * @returns Object: Step which was just added
         */
        self.addStep = function (stepName, condition) {
            var step = {
                index: self.steps.length,
                name: stepName,
                validations: {},
                enabled: condition ? condition : ALWAYS_TRUE,
                setupValidations: setupValidations,
                listeners: []
            };
            Object.defineProperty(step, 'state', {get: getStepState});
            Object.defineProperty(step, 'next', {get: getNextStep});
            self.steps.push(step);
            if (stepIdx < 0) stepIdx = self.steps.length-1;
            return step;
        };

        var getStepState = function () {
            if (stepIdx > this.index) return 'past';
            else if (stepIdx < this.index) return 'future';
            else return 'active';
        };

        var getNextStep = function () {
            return self.steps[this.index+1];
        };

        var setupValidations = function(allowNext, allowPrev){
            this.validations = {
                next: allowNext,
                prev: allowPrev
            };
        };

        /**
         * Navigate to the previous step.  If this is not possible, the method has no effect.
         * @returns the current step after the function has executed.
         */
        self.prevStep = function () {
            if (loading || done || self.atFirstStep() || !self.allowPrev()) return self.stepName;
            stepIdx--;
            while (stepIdx >= 0 && !self.step.enabled())
                stepIdx--;
            fireListeners();
            return self.step;
        };

        /**
         * Navigate to the next step.  If this is not possible, the method has no effect.
         *
         * If the step has a submit method, this will be invoked.  However, if the submit method returns a step name
         * (or a promise to one), the wizard will go to that step instead.
         *
         * @returns the a promise to the current step after the function has executed.
         */
        self.nextStep = function () {
            if (loading || done || !self.allowNext()) return $q.when(self.stepName);

            loading = true;
            return $q.when(self.step.submit ? self.step.submit() : undefined)
                .then(function(){
                    loading = false;
                    if (!self.atLastStep()) {
                        stepIdx++;
                        while (!self.step.enabled() && stepIdx < self.steps.length - 1)
                            stepIdx++;
                        fireListeners();
                    }
                    return self.step;
                })
                .catch(function(submitError){
                    if (submitError && submitError.goToStep) {
                        loading = false;
                        return self.goToStep(submitError.goToStep);
                    }
                    else throw submitError;
                });
        };

        /**
         * Navigate to the given step (if found)  If not possible, the method has no effect.
         * @param stepName name of the step to navigate to
         * @return a promise to the current step after navigating
         */
        self.goToStep = function (stepName) {
            var targetStep = findStep(stepName);
            if (loading || done || !targetStep || !targetStep.enabled()) return $q.when(self.stepName);
            loading = true;
            var navigate = function(){
                if (targetStep.index < stepIdx) {
                    if (!self.allowPrev()) return $q.when();
                    stepIdx--;
                    while (!self.step.enabled() && stepIdx > 0)
                        stepIdx--;
                    return $q.when(navigate());
                }
                else if (targetStep.index > stepIdx) {
                    if (!self.allowNext()) return $q.when();
                    return $q.when(self.step.submit ? self.step.submit() : undefined)
                        .then(function(){
                            stepIdx++;
                            while (!self.step.enabled() && stepIdx < self.steps.length-1)
                                stepIdx++;
                            return navigate();
                        })
                        .catch(function(submitError){
                            if (submitError && submitError.goToStep)
                                targetStep = findStep(submitError.goToStep) || targetStep;
                            else throw submitError;
                        });
                }
                else return $q.when();
            };

            return navigate().then(function(){
                fireListeners();
                loading = false;
                return self.step;
            });
        };

        /**
         * Navigate to the first enabled step.  If not possible, the method has no effect.
         * @return the current step after navigating
         */
        self.reset = function () {
            if (loading) return self.step;
            var targetStep = nextEnabled(self.steps[0]);
            if (done || !targetStep)
                stepIdx = -1;
            else
                stepIdx = targetStep.index;
            fireListeners();
            return self.step;
        };

        /**
         * Finalize the wizard.  After this, calls to @see done will return true. Stepping will
         * have no further effect.
         */
        self.finalize = function () {
            stepIdx = -1;
            done = true;
        };

        /**
         * @returns the logical step index.  This index is one-based and skips disabled steps.
         */
        self.getStepIndex = function () {
            var r = 0;
            for (var i = stepIdx; i >= 0; i--)
                if (self.steps[i].enabled()) r++;
            return r;
        };

        self.allowNext = function() {
            if (!self.step.validations.next) return true;
            return self.step.validations.next();
        };

        self.allowPrev = function() {
            if (!self.step.validations.prev) return true;
            return self.step.validations.prev();
        };

        /**
         * @returns {boolean} whether the wizard is at the first valid step.
         */
        self.atFirstStep = function () {
            for (var i = stepIdx - 1; i >= 0; i--) {
                if (self.steps[i].enabled())
                    return false;
            }
            return true;
        };

        /**
         * @returns {boolean} whether the wizard is at the last valid step.
         */
        self.atLastStep = function () {
            for (var i = stepIdx + 1; i < self.steps.length; i++) {
                if (self.steps[i].enabled())
                    return false;
            }
            return true;
        };

        /**
         * @returns {boolean} whether the wizard is done (@see finalize)
         */
        self.done = function () {
            return done;
        };

        /**
         * Fire all listeners for the current step for 'enter', and the
         * previously fired step for 'exit'.
         */
        var previouslyFiredStepIdx = -1;
        var fireListeners = function () {
            if (stepIdx === previouslyFiredStepIdx) return;
            var previousStep = self.steps[previouslyFiredStepIdx];
            var newStep = self.steps[stepIdx];
            if (previousStep) {
                previousStep.listeners.forEach(function (listener) {
                    listener.call(self, 'exit', previousStep);
                });
                self.listeners.forEach(function (listener) {
                    listener.call(self, 'exit', previousStep);
                });
            }
            if (newStep) {
                newStep.listeners.forEach(function (listener) {
                    listener.call(self, 'enter', newStep);
                });
                self.listeners.forEach(function (listener) {
                    listener.call(self, 'enter', newStep);
                });
            }
            previouslyFiredStepIdx = stepIdx;
        };

        /**
         * Find a step with the given name
         * @return the step if found, else undefined;
         */
        var findStep = function(stepName){
            for (var i=0; i<self.steps.length; i++){
                if (self.steps[i].name === stepName)
                    return self.steps[i];
            }
            return undefined;
        };

        /**
         * Find the first step, after or including the given one, that is enabled.
         * If none found, return undefined.
         */
        var nextEnabled = function(step){
            while (step && !step.enabled())
                step = step.next;
            return step;
        };
    };
}());
